import { useState, useCallback, useRef } from 'react';
import { Student } from '@/services/db/types';
import { StudentData } from '../types';
import { unifiedStudentStorage } from '@/services/storage/unifiedStudentStorage';

/**
 * Enhanced student operations hook using unified storage for 5000+ students
 */
export const useStudentOperations = () => {
  const [students, setStudents] = useState<StudentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const initializingRef = useRef(false);

  const refreshStudents = useCallback(async () => {
    try {
      console.log('🔄 Refreshing students via unified storage...');
      
      const fetchedStudents = await unifiedStudentStorage.getAllStudents();
      console.log(`✅ Loaded ${fetchedStudents.length} students from unified storage`);
      
      const mappedStudents = fetchedStudents.map(student => ({
        ...student,
        id: student.id || 0,
        programs: student.programs || []
      })) as StudentData[];
      
      setStudents(mappedStudents);
      console.log(`✅ Set ${mappedStudents.length} students in context via unified storage`);
      
      return fetchedStudents;
    } catch (error) {
      console.error('❌ Error in refreshStudents via unified storage:', error);
      setStudents([]);
      return [];
    }
  }, []);

  const initializeStudentData = useCallback(async () => {
    if (initialized || initializingRef.current) {
      console.log('🔄 Already initialized or initializing, skipping...');
      return;
    }
    
    initializingRef.current = true;
    console.log('🚀 Initializing student data via unified storage...');
    setLoading(true);
    
    try {
      await unifiedStudentStorage.initialize();
      await refreshStudents();
      setInitialized(true);  
      console.log('✅ Student data initialized successfully via unified storage');
    } catch (error) {
      console.error('❌ Error initializing student data:', error);
      setInitialized(true);
    } finally {
      setLoading(false);
      initializingRef.current = false;
    }
  }, [refreshStudents]);

  const getStudent = useCallback((id: number) => {
    const student = students.find(student => student.id === id);
    console.log(`🔍 Getting student ID ${id} from context:`, student ? 'Found' : 'Not found');
    return student;
  }, [students]);

  const addStudent = useCallback(async (studentData: Omit<StudentData, 'id' | 'createdAt' | 'updatedAt'>): Promise<number> => {
    console.log('➕ Adding new student via unified storage:', studentData.name);
    try {
      const newStudent = await unifiedStudentStorage.addStudent(studentData);
      
      // Update local state immediately
      const studentForState: StudentData = {
        ...newStudent,
        programs: newStudent.programs || []
      };
      
      setStudents(prevStudents => [...prevStudents, studentForState]);
      
      console.log('✅ Student added successfully via unified storage:', newStudent.id);
      return newStudent.id;
    } catch (error) {
      console.error('❌ Error adding student via unified storage:', error);
      throw error;
    }
  }, []);

  const updateStudent = useCallback(async (id: number, updatedData: Omit<StudentData, 'id' | 'createdAt' | 'updatedAt'>) => {
    console.log('✏️ Updating student ID via unified storage:', id);
    try {
      const updatedStudent = await unifiedStudentStorage.updateStudent(id, updatedData);
      
      if (updatedStudent) {
        // Update local state
        setStudents(prevStudents => 
          prevStudents.map(student => 
            student.id === id 
              ? { ...updatedStudent, programs: updatedStudent.programs || [] }
              : student
          )
        );
        
        console.log('✅ Student updated successfully via unified storage');
      }
    } catch (error) {
      console.error('❌ Error updating student via unified storage:', error);
      throw error;
    }
  }, []);

  const deleteStudent = useCallback(async (id: number) => {
    console.log('🗑️ Deleting student ID via unified storage:', id);
    try {
      await unifiedStudentStorage.deleteStudent(id);
      
      // Update local state
      setStudents(prevStudents => prevStudents.filter(student => student.id !== id));
      
      console.log('✅ Student deleted successfully via unified storage');
    } catch (error) {
      console.error('❌ Error deleting student via unified storage:', error);
      throw error;
    }
  }, []);

  const searchStudents = useCallback(async (query: string): Promise<StudentData[]> => {
    console.log('🔍 Searching students via unified storage:', query);
    try {
      const results = await unifiedStudentStorage.searchStudents(query);
      return results as StudentData[];
    } catch (error) {
      console.error('❌ Error searching students:', error);
      return [];
    }
  }, []);

  const saveStudent = useCallback(async (studentData: Omit<StudentData, 'id' | 'createdAt' | 'updatedAt'>) => {
    await addStudent(studentData);
  }, [addStudent]);

  const saveStudentList = useCallback(async (studentList: StudentData[]) => {
    console.log('📥 Saving student list via unified storage:', studentList.length);
    
    try {
      // Clear existing data first
      const existingStudents = await unifiedStudentStorage.getAllStudents();
      for (const student of existingStudents) {
        await unifiedStudentStorage.deleteStudent(student.id);
      }
      
      // Add new data
      for (const student of studentList) {
        await unifiedStudentStorage.addStudent(student);
      }
      
      // Update local state
      const mappedStudents = studentList.map(student => ({
        ...student,
        id: student.id || 0,
        programs: student.programs || []
      })) as StudentData[];
      
      setStudents(mappedStudents);
      console.log('✅ Student list saved via unified storage');
    } catch (error) {
      console.error('❌ Error saving student list:', error);
      throw error;
    }
  }, []);

  const loadBackup = useCallback(async (data: StudentData[]) => {
    if (Array.isArray(data) && data.length > 0) {
      console.log('📥 Loading backup data via unified storage:', data.length);
      await saveStudentList(data);
    } else {
      console.error('❌ Invalid backup data format');
    }
  }, [saveStudentList]);

  return {
    students,
    loading,
    initialized,
    getStudent,
    addStudent,
    updateStudent,
    deleteStudent,
    searchStudents,
    refreshStudents,
    initializeStudentData,
    saveStudent,
    saveStudentList,
    loadBackup
  };
};
